import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const AlertsContext = createContext(null);
const POLL_MS = 3000;

// Client-side consumer of the backend Alert Engine: alerts CRUD + polled trigger events → toast / badge / desktop.
export function AlertsProvider({ children }) {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [managerOpen, setManagerOpen] = useState(false);
  const [draft, setDraft] = useState(null); // prefill for the create form
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());

  const refreshAlerts = useCallback(async () => {
    try { const { data } = await api.get("/alerts"); setAlerts(data.alerts); } catch {}
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts/events", { params: { limit: 100 } });
      setEvents(data.events);
      setUnread(data.unread);
      data.events.forEach((e) => seenRef.current.add(e.id));
      sinceRef.current = data.server_time;
    } catch {}
  }, []);

  const notify = useCallback((e) => {
    toast(`${e.display} · ${e.kind.toUpperCase()}`, { description: e.message, duration: 8000 });
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification(`Fela R.I.O — ${e.display}`, { body: e.message, tag: e.id }); } catch {}
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshAlerts();
    loadEvents();
    if (typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission().catch(() => {});
    const id = setInterval(async () => {
      if (!sinceRef.current) return;
      try {
        const { data } = await api.get("/alerts/events", { params: { since: sinceRef.current, limit: 50 } });
        sinceRef.current = data.server_time;
        setUnread(data.unread);
        const fresh = data.events.filter((e) => !seenRef.current.has(e.id));
        if (!fresh.length) return;
        fresh.forEach((e) => { seenRef.current.add(e.id); notify(e); });
        setEvents((prev) => [...fresh, ...prev].slice(0, 200));
        refreshAlerts();
      } catch {}
    }, POLL_MS);
    return () => clearInterval(id);
  }, [user, refreshAlerts, loadEvents, notify]);

  const createAlert = useCallback(async (body) => {
    const { data } = await api.post("/alerts", body);
    setAlerts((prev) => [data.alert, ...prev]);
    return data.alert;
  }, []);
  const updateAlert = useCallback(async (id, patch) => {
    const { data } = await api.put(`/alerts/${id}`, patch);
    setAlerts((prev) => prev.map((a) => (a.id === id ? data.alert : a)));
    return data.alert;
  }, []);
  const deleteAlert = useCallback(async (id) => {
    await api.delete(`/alerts/${id}`);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);
  const markRead = useCallback(async (ids) => {
    await api.post("/alerts/events/read", { ids: ids ?? null });
    setEvents((prev) => prev.map((e) => (!ids || ids.includes(e.id) ? { ...e, read: true } : e)));
    setUnread((u) => (ids ? Math.max(0, u - ids.length) : 0));
  }, []);

  const openManager = useCallback((prefill = null) => { setDraft(prefill); setManagerOpen(true); }, []);

  return (
    <AlertsContext.Provider value={{ alerts, events, unread, managerOpen, setManagerOpen, openManager, draft, createAlert, updateAlert, deleteAlert, markRead, refreshAlerts }}>
      {children}
    </AlertsContext.Provider>
  );
}

export const useAlerts = () => useContext(AlertsContext);
